import { useCallback, useEffect, useMemo, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as yup from 'yup';

import { teamsEndpoints } from '@/api/endpoints';
import FormDialog from '@/components/atoms/FormDialog/FormDialog';
import { MultiSelectField, type MultiSelectOption } from '@/components/atoms/MultiSelect';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useCloseModal } from '@/hooks/useCloseModal';
import { useRoles } from '@/hooks/useRoles';
import { useTeamRoles } from '@/hooks/useTeamRoles';
import { useTeams } from '@/hooks/useTeams';
import { getPermissions } from '@/redux/permissions/selectors';
import { OptionBase } from '@/shared/types/common';
import { actions } from '@/shared/types/permissions';
import { Role } from '@/shared/types/role';
import { RootState } from '@/shared/types/state';
import { Team } from '@/shared/types/team';
import { roleSelectOptions } from '@/util/roleOptions';
import { submitWithTrimming } from '@/util/util';
import { LENGTH_M } from '@/util/validation/constants';
import { errorMessage } from '@/util/validation/helpers';
import { invitationRules } from '@/util/validation/rules/invitation-rules';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

interface Form {
  roles: OptionBase[];
  team_name: string;
  team_description: string;
}

interface Props {
  team: Team;
}

const ModalTeam = ({ team }: Props) => {
  const { t } = useTranslation();
  const { team_id, name, description } = team || {};

  const parentParams = team_id ? { team_id } : undefined;
  const teamSchema = yup.object({
    team_name: createNameFieldSchema({
      getEndpoint: (name) => teamsEndpoints.checkName(name, parentParams),
      excludeName: team_id ? name : undefined,
    }),
    team_description: yup.string().max(LENGTH_M),
    roles: yup.array().of(yup.mixed<OptionBase>()),
  });

  const formMethods = useForm<Form>({
    mode: 'onChange',
    defaultValues: { roles: [], team_name: name, team_description: description },
    resolver: yupResolver(teamSchema) as Resolver<Form>,
  });

  const {
    control,
    setValue,
    formState: { errors },
  } = formMethods;

  const closeModal = useCloseModal();
  const [isLoading, setIsLoading] = useState(false);

  const canAssignRoles = useSelector((state: RootState) =>
    getPermissions(state, 'roles', actions.ASSIGN)
  );

  const { roles, availableRoles } = useRoles();
  const { createTeam, updateTeam } = useTeams();

  const { removeRole, assignRoles, roles: teamRoles } = useTeamRoles(team_id);

  useEffect(() => {
    if (!teamRoles?.length) {
      return;
    }

    const newOptions = teamRoles
      ?.map((r) => {
        const role = roles.find((availableRole) => r.role_id === availableRole.role_id);
        if (!role) {
          return null;
        }
        return { value: r.role_id, label: role.name };
      })
      .filter(Boolean) as OptionBase[];

    setValue('roles', newOptions);
  }, [roles, teamRoles, setValue]);

  const onSubmit = useCallback(
    async (form: Form) => {
      setIsLoading(true);
      const newRoleids = canAssignRoles ? (form.roles.map((r) => r.value) as string[]) : [];

      const newTeam = {
        name: form.team_name,
        description: form.team_description,
        role_ids: newRoleids,
        team_id: team?.team_id,
      };
      if (team) {
        try {
          if (team.description !== form.team_description || team.name !== form.team_name) {
            updateTeam(newTeam, { onError: () => setIsLoading(false) });
          }
          if (canAssignRoles) {
            const addedRoles = newRoleids.filter(
              (role) => teamRoles?.findIndex((r) => r.role_id === role) === -1
            );

            if (addedRoles.length > 0) {
              await assignRoles({ role_ids: newRoleids, team_id: team_id });
            }
            const removedRoles = teamRoles?.filter((role) => !newRoleids.includes(role.role_id));

            if (removedRoles.length > 0) {
              await Promise.all(
                removedRoles.map((role: Role) =>
                  removeRole({ team_id: team.team_id, role_id: role.role_id })
                )
              );
            }
          }

          closeModal();
        } catch {
          setIsLoading(false);
        }
      } else {
        createTeam(newTeam, {
          // Close once the team exists, with or without roles — a team created without
          // roles used to leave the dialog open on a spinner that never stopped.
          onSuccess: async (resp) => {
            if (newTeam.role_ids.length > 0) {
              await assignRoles({ role_ids: newTeam.role_ids, team_id: resp.team_id }).catch(
                () => null
              );
            }
            closeModal();
          },
          onError: () => setIsLoading(false),
        });
      }
    },
    [
      assignRoles,
      canAssignRoles,
      createTeam,
      closeModal,
      removeRole,
      team,
      teamRoles,
      team_id,
      updateTeam,
    ]
  );

  const multiSelectOptions: MultiSelectOption[] = useMemo(
    () =>
      roleSelectOptions(availableRoles, roles, {
        system: t('roles.system_roles'),
        custom: t('roles.custom_roles'),
      }),
    [availableRoles, roles, t]
  );

  return (
    <FormDialog
      title={team_id ? t('teams.edit') : t('teams.create_team')}
      submitLabel={team_id ? t('common.save') : t('common.create')}
      onSubmit={submitWithTrimming(formMethods, onSubmit)}
      isLoading={isLoading}
      preventClose
      submitOnEnter
      contentClassName="sm:max-w-2xl"
    >
      <Form {...formMethods}>
        <form autoComplete="off" className="space-y-6">
          <div className="input__container">
            <FormField
              control={control}
              name="team_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('field.name')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      placeholder={t('common.name_placeholder')}
                      autoFocus
                    />
                  </FormControl>
                  <FormMessage>{errorMessage({ field: errors.team_name })}</FormMessage>
                </FormItem>
              )}
            />
          </div>
          <div className="input__container">
            <FormField
              control={control}
              name="team_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('field.description')}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      placeholder={t('common.description_placeholder')}
                    />
                  </FormControl>
                  <FormMessage>{errorMessage({ field: errors.team_description })}</FormMessage>
                </FormItem>
              )}
            />
          </div>
          {canAssignRoles && (
            <div className="input__container">
              <FormField
                control={control}
                name="roles"
                rules={!team_id ? invitationRules(t).roles : {}}
                render={({ field }) => {
                  const selectedValues = field.value?.map((role: OptionBase) => role.value) || [];

                  return (
                    <FormItem>
                      <FormLabel className="text-base font-medium">{t('teams.roles')}</FormLabel>
                      <FormControl>
                        <MultiSelectField
                          title={t('common.select')}
                          options={multiSelectOptions}
                          value={selectedValues}
                          onChange={(selectedStringValues) => {
                            const selectedRoles = selectedStringValues
                              .map((value) => availableRoles.find((role) => role.value === value))
                              .filter((role): role is OptionBase => !!role);
                            field.onChange(selectedRoles);
                          }}
                          placeholder={t('roles.select')}
                          disabled={!roles}
                          error={!!errors?.roles?.message}
                        />
                      </FormControl>
                      {errors?.roles?.message && (
                        <FormMessage>{errorMessage({ field: errors.roles })}</FormMessage>
                      )}
                    </FormItem>
                  );
                }}
              />
            </div>
          )}
        </form>
      </Form>
    </FormDialog>
  );
};

export default ModalTeam;
